/**
 * Global achievement celebration — detects newly-unlocked badges and fires
 * a full-screen confetti animation + toast with share CTA.
 *
 * Mount once per Ads Creator layout. Works two ways:
 *   1. Listens to `window` event `ads:achievement-unlocked` fired by any
 *      component that knows it triggered an unlock (e.g. InstantCampaign).
 *   2. Light polling fallback — every 60s compares the set of unlocked_ids
 *      against a localStorage snapshot. If new IDs appear, celebrates each.
 *
 * Zero deps beyond lucide + sonner. Pure CSS confetti (no libraries).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Share2, Award } from "lucide-react";
import { adsApiCall } from "./api";

const SNAPSHOT_KEY = "ads:achievements:unlocked_snapshot";
const POLL_INTERVAL_MS = 60_000;

function loadSnapshot() {
  try { return new Set(JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveSnapshot(ids) {
  try { localStorage.setItem(SNAPSHOT_KEY, JSON.stringify([...ids])); } catch {}
}

/** Confetti piece presets — vary shapes, angles, delays for organic feel */
const CONFETTI = Array.from({ length: 60 }).map((_, i) => ({
  id: i,
  left: Math.random() * 100,
  delay: Math.random() * 0.3,
  duration: 2 + Math.random() * 1.8,
  hue: Math.floor(Math.random() * 360),
  size: 6 + Math.random() * 10,
  rotate: Math.random() * 720 - 360,
  translateX: Math.random() * 200 - 100,
}));

function ConfettiBurst({ show }) {
  if (!show) return null;
  return (
    <div className="ads-confetti-burst" aria-hidden="true" data-testid="ads-confetti-burst">
      {CONFETTI.map((c) => (
        <span
          key={c.id}
          className="ads-confetti-piece"
          style={{
            left: `${c.left}%`,
            width: c.size, height: c.size,
            background: `hsl(${c.hue}, 90%, 60%)`,
            animationDelay: `${c.delay}s`,
            animationDuration: `${c.duration}s`,
            "--ads-confetti-rotate": `${c.rotate}deg`,
            "--ads-confetti-x": `${c.translateX}px`,
          }}
        />
      ))}
    </div>
  );
}

function UnlockToast({ badge, onShare, onDismiss }) {
  return (
    <div className="ads-unlock-toast" data-testid={`ads-unlock-toast-${badge.id}`}>
      <div className="ads-unlock-toast-glow" aria-hidden="true" />
      <div className="ads-unlock-toast-icon"><Award size={28} /></div>
      <div className="ads-unlock-toast-body">
        <div className="ads-unlock-toast-kicker">ACHIEVEMENT UNLOCKED</div>
        <div className="ads-unlock-toast-label">{badge.label}</div>
        <div className="ads-unlock-toast-desc">{badge.description}</div>
      </div>
      <div className="ads-unlock-toast-actions">
        <button
          className="ads-btn ads-btn-primary"
          style={{ padding: "6px 12px", fontSize: 12 }}
          onClick={() => onShare(badge)}
          data-testid={`ads-unlock-share-${badge.id}`}
        >
          <Share2 size={13} /> Share
        </button>
        <button className="ads-unlock-toast-close" onClick={onDismiss} data-testid={`ads-unlock-close-${badge.id}`} aria-label="Dismiss">×</button>
      </div>
    </div>
  );
}

export default function AchievementCelebration() {
  // Queue of newly-unlocked badges to celebrate, one at a time
  const [queue, setQueue] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const activeRef = useRef(false);

  const fetchAndDiff = useCallback(async () => {
    try {
      // Skip silently if the user isn't signed in — the polling endpoint
      // returns 401 for anon, which floods the console with noise.
      if (!localStorage.getItem("access_token")) return;
      const data = await adsApiCall.achievementsMine();
      if (!data?.badges) return;
      const unlockedNow = new Set(
        data.badges.filter((b) => b.unlocked).map((b) => b.id),
      );
      const snapshot = loadSnapshot();
      // If snapshot is empty, this is the user's first visit —
      // seed it silently without celebrating existing unlocks.
      if (snapshot.size === 0 && unlockedNow.size > 0) {
        saveSnapshot(unlockedNow);
        return;
      }
      const newlyUnlocked = [...unlockedNow].filter((id) => !snapshot.has(id));
      if (newlyUnlocked.length > 0) {
        const metaById = Object.fromEntries(data.badges.map((b) => [b.id, b]));
        setQueue((q) => [...q, ...newlyUnlocked.map((id) => metaById[id]).filter(Boolean)]);
        saveSnapshot(unlockedNow);
      }
    } catch { /* ignore — polling is best-effort */ }
  }, []);

  // Poll periodically
  useEffect(() => {
    // Fire once on mount, then interval
    fetchAndDiff();
    const t = setInterval(fetchAndDiff, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchAndDiff]);

  // Custom event listener — InstantCampaign + other emitters push directly
  useEffect(() => {
    const onEvent = (e) => {
      const payload = e.detail;
      if (!payload) return;
      const badges = Array.isArray(payload) ? payload : [payload];
      if (badges.length === 0) return;
      setQueue((q) => [...q, ...badges]);
      // Update snapshot so polling won't re-fire
      const snap = loadSnapshot();
      badges.forEach((b) => b?.id && snap.add(b.id));
      saveSnapshot(snap);
    };
    window.addEventListener("ads:achievement-unlocked", onEvent);
    return () => window.removeEventListener("ads:achievement-unlocked", onEvent);
  }, []);

  // Process queue — show one at a time with confetti + 6s auto-dismiss
  useEffect(() => {
    if (activeRef.current || queue.length === 0) return;
    activeRef.current = true;
    setShowConfetti(true);
    const t1 = setTimeout(() => setShowConfetti(false), 3500);
    const t2 = setTimeout(() => {
      setQueue((q) => q.slice(1));
      activeRef.current = false;
    }, 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [queue]);

  const current = queue[0];

  const share = useCallback(async (badge) => {
    const url = badge.email_hash
      ? `${window.location.origin}/ads-creator/a/${badge.email_hash}/${badge.id}`
      : window.location.href;
    const text = `I just unlocked "${badge.label}" on TopRankPulse Studio! 🏆`;
    if (navigator.share) {
      try { await navigator.share({ title: badge.label, text, url }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Copied to clipboard");
    } catch {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank");
    }
  }, []);

  const dismiss = useCallback(() => {
    setQueue((q) => q.slice(1));
    activeRef.current = false;
    setShowConfetti(false);
  }, []);

  return (
    <>
      <ConfettiBurst show={showConfetti && !!current} />
      {current && <UnlockToast badge={current} onShare={share} onDismiss={dismiss} />}
    </>
  );
}
